// @ts-nocheck
import { Calendar, Download, Plus, Trash2, AlertCircle, Clock } from 'lucide-react'
import { useState } from 'react'
import { useApi } from '../../hooks/useApi'
import TimeSlotEditor from './TimeSlotEditor'

export default function HolidayManager({ phoneNumberId, holidays, onChange }) {
  const api = useApi()
  const [showAddForm, setShowAddForm] = useState(false)
  const [importing, setImporting] = useState(false)
  const [newHoliday, setNewHoliday] = useState({
    date: '',
    name: '',
    isOpen: false,
    hours: [],
  })
  const [editingHolidayDate, setEditingHolidayDate] = useState(null)

  // 日本の祝日をインポート
  const handleImportJapanHolidays = async () => {
    if (!confirm('日本の祝日（今年と来年）をインポートしますか？\n既存の祝日は上書きされます。')) {
      return
    }

    try {
      setImporting(true)
      
      // 日本の祝日テンプレートを取得
      const data = await api.get('/api/holidays/templates/japan')
      
      // 既存の祝日とマージ（重複を削除）
      const existingDates = new Set(holidays.map((h) => h.date))
      const newHolidays = data.holidays.filter((h) => !existingDates.has(h.date))
      
      onChange([...holidays, ...newHolidays])
      alert(`${newHolidays.length}件の祝日をインポートしました`)
    } catch (err) {
      console.error('インポートエラー:', err)
      alert(`エラー: ${err.message}`)
    } finally {
      setImporting(false)
    }
  }

  // 祝日を追加
  const handleAddHoliday = () => {
    if (!newHoliday.date || !newHoliday.name) {
      alert('日付と名称を入力してください')
      return
    }

    // 重複チェック
    if (holidays.some((h) => h.date === newHoliday.date)) {
      alert('この日付はすでに登録されています')
      return
    }

    onChange([...holidays, { ...newHoliday }])
    setNewHoliday({
      date: '',
      name: '',
      isOpen: false,
      hours: [],
    })
    setShowAddForm(false)
  }

  // 祝日を削除
  const handleDeleteHoliday = (date) => {
    if (confirm('この祝日を削除しますか？')) {
      onChange(holidays.filter((h) => h.date !== date))
    }
  }

  // 祝日の営業設定を変更
  const handleToggleHolidayOpen = (date) => {
    const holiday = holidays.find((h) => h.date === date)
    if (!holiday) return

    if (!holiday.isOpen) {
      // 休業日→営業日: デフォルト時間を設定して編集モードに
      onChange(
        holidays.map((h) =>
          h.date === date
            ? {
                ...h,
                isOpen: true,
                hours: [{ start: '10:00', end: '17:00' }],
              }
            : h
        )
      )
      setEditingHolidayDate(date)
    } else {
      // 営業日→休業日
      onChange(
        holidays.map((h) =>
          h.date === date
            ? {
                ...h,
                isOpen: false,
                hours: [],
              }
            : h
        )
      )
      setEditingHolidayDate(null)
    }
  }

  // 祝日の営業時間を更新
  const handleUpdateHolidayHours = (date, hours) => {
    onChange(
      holidays.map((h) =>
        h.date === date
          ? {
              ...h,
              hours,
            }
          : h
      )
    )
  }

  // 時間帯を追加
  const handleAddTimeSlot = (date) => {
    const holiday = holidays.find((h) => h.date === date)
    if (!holiday) return

    onChange(
      holidays.map((h) =>
        h.date === date
          ? {
              ...h,
              hours: [...h.hours, { start: '10:00', end: '17:00' }],
            }
          : h
      )
    )
  }

  // 時間帯を削除
  const handleRemoveTimeSlot = (date, index) => {
    const holiday = holidays.find((h) => h.date === date)
    if (!holiday || holiday.hours.length <= 1) return

    onChange(
      holidays.map((h) =>
        h.date === date
          ? {
              ...h,
              hours: h.hours.filter((_, i) => i !== index),
            }
          : h
      )
    )
  }

  // 時間帯を変更
  const handleChangeTimeSlot = (date, index, timeSlot) => {
    onChange(
      holidays.map((h) =>
        h.date === date
          ? {
              ...h,
              hours: h.hours.map((slot, i) => (i === index ? timeSlot : slot)),
            }
          : h
      )
    )
  }

  // 日付でソート
  const sortedHolidays = [...holidays].sort((a, b) => a.date.localeCompare(b.date))

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h3 className="text-md font-semibold text-gray-900 dark:text-white">祝日・特別日</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                祝日や特別な営業日を管理します
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleImportJapanHolidays}
              disabled={importing}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {importing ? 'インポート中...' : '日本の祝日'}
            </button>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
            >
              <Plus className="w-4 h-4" />
              手動で追加
            </button>
          </div>
        </div>
      </div>

      {/* 追加フォーム */}
      {showAddForm && (
        <div className="p-6 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
            祝日を追加
          </h4>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  日付 <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={newHoliday.date}
                  onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  名称 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newHoliday.name}
                  onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                  placeholder="例: 元日"
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={newHoliday.isOpen}
                onChange={(e) =>
                  setNewHoliday({
                    ...newHoliday,
                    isOpen: e.target.checked,
                    hours: e.target.checked ? [{ start: '09:00', end: '18:00' }] : [],
                  })
                }
                className="w-4 h-4 text-red-600 rounded focus:ring-2 focus:ring-red-500"
              />
              <label className="text-sm text-gray-700 dark:text-gray-300">
                この日は営業する（短縮営業など）
              </label>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowAddForm(false)}
                className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                キャンセル
              </button>
              <button
                onClick={handleAddHoliday}
                className="px-3 py-2 text-sm bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
              >
                追加
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 祝日リスト */}
      <div className="p-6">
        {sortedHolidays.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              登録されている祝日がありません
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              「日本の祝日」ボタンでインポートできます
            </p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {sortedHolidays.map((holiday) => (
              <div
                key={holiday.date}
                className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <input
                      type="checkbox"
                      checked={holiday.isOpen}
                      onChange={() => handleToggleHolidayOpen(holiday.date)}
                      className="mt-0.5 w-4 h-4 text-red-600 rounded focus:ring-2 focus:ring-red-500"
                      title={holiday.isOpen ? '営業する' : '休業日'}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {holiday.name}
                        </span>
                        {holiday.isOpen && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 rounded">
                            営業
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {holiday.date}
                      </span>

                      {/* 営業時間設定 */}
                      {holiday.isOpen && (
                        <div className="mt-3 space-y-2">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                            <Clock className="w-3 h-3" />
                            営業時間
                            {editingHolidayDate === holiday.date && (
                              <button
                                onClick={() => setEditingHolidayDate(null)}
                                className="ml-auto text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                完了
                              </button>
                            )}
                            {editingHolidayDate !== holiday.date && (
                              <button
                                onClick={() => setEditingHolidayDate(holiday.date)}
                                className="ml-auto text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                編集
                              </button>
                            )}
                          </div>

                          {editingHolidayDate === holiday.date ? (
                            <div className="space-y-2 pl-5">
                              {holiday.hours.map((timeSlot, index) => (
                                <TimeSlotEditor
                                  key={index}
                                  timeSlot={timeSlot}
                                  onChange={(slot) => handleChangeTimeSlot(holiday.date, index, slot)}
                                  onRemove={() => handleRemoveTimeSlot(holiday.date, index)}
                                  canRemove={holiday.hours.length > 1}
                                />
                              ))}
                              <button
                                onClick={() => handleAddTimeSlot(holiday.date)}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                              >
                                <Plus className="w-3 h-3" />
                                時間帯を追加
                              </button>
                            </div>
                          ) : (
                            <div className="pl-5 text-xs text-gray-600 dark:text-gray-400">
                              {holiday.hours.map((slot, index) => (
                                <div key={index}>
                                  {slot.start} 〜 {slot.end}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteHoliday(holiday.date)}
                    className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="削除"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ヘルプ */}
      <div className="px-6 pb-6">
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex gap-2">
          <AlertCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-800 dark:text-blue-300">
            祝日は通常休業日として扱われますが、チェックを入れることで営業日に設定できます。
            祝日でも営業する場合は、短縮営業などの設定が可能です。
          </p>
        </div>
      </div>
    </div>
  )
}
